// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import api from './advisorApi';
import type {
  CalculatePreviewResponse,
  CalculationResult,
  MaxFundingScenarioInputs,
  ReportType,
  SaveCalculationResultRequest,
} from '../types/calculationResult';

export const calculationResultApi = {
  list: async (
    profileId: string,
    reportType: ReportType = 'MAXFUNDING',
    signal?: AbortSignal,
  ): Promise<CalculationResult[]> => {
    const response = await api.get<CalculationResult[]>(
      `/profiles/${profileId}/calculation-results`,
      { params: { reportType }, signal },
    );
    return response.data;
  },

  get: async (
    profileId: string,
    id: string,
    signal?: AbortSignal,
  ): Promise<CalculationResult> => {
    const response = await api.get<CalculationResult>(
      `/profiles/${profileId}/calculation-results/${id}`,
      { signal },
    );
    return response.data;
  },

  create: async (
    profileId: string,
    body: SaveCalculationResultRequest,
  ): Promise<CalculationResult> => {
    const response = await api.post<CalculationResult>(
      `/profiles/${profileId}/calculation-results`,
      body,
    );
    return response.data;
  },

  update: async (
    profileId: string,
    id: string,
    body: SaveCalculationResultRequest,
    signal?: AbortSignal,
  ): Promise<CalculationResult> => {
    const response = await api.put<CalculationResult>(
      `/profiles/${profileId}/calculation-results/${id}`,
      body,
      { signal },
    );
    return response.data;
  },

  remove: async (profileId: string, id: string): Promise<void> => {
    await api.delete(`/profiles/${profileId}/calculation-results/${id}`);
  },

  calculate: async (
    reportType: ReportType,
    inputs: MaxFundingScenarioInputs,
    signal?: AbortSignal,
  ): Promise<CalculatePreviewResponse> => {
    const response = await api.post<CalculatePreviewResponse>(
      `/calculation-results/calculate`,
      { reportType, inputs },
      { signal },
    );
    return response.data;
  },
};
